import React, { useEffect, useState } from "react";

import { UnitFormatsProvider } from "@strava/units/react";
import { UNIT_METRIC, type UnitSystem } from "@strava/units";
import { Map } from "@strava/feature-global-wasm-mre";
import I18n from "../../utils/I18n";
import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";
import { buildAthletes } from "./SegmentAnalysisMap.helpers";
import { AthleteRabbit as AthleteRabbitType, EventDispatcher, RosterEntries } from "./SegmentAnalysisMap.types";
import { AthleteRabbit } from "./components/AthleteRabbit/AthleteRabbit";
import styles from "./SegmentAnalysisMap.scss";

interface SegmentAnalysisMapProps {
  latlngStream: [number, number][];
  rosterEntries: RosterEntries;
  eventDispatcher: EventDispatcher;
  measurementUnits?: UnitSystem;
}

const SEGMENT_EFFORT_COMPARISON_ANALYTICS_CATEGORY = 'segments';
const SEGMENT_EFFORT_COMPARISON_ANALYTICS_PAGE = 'segment_effort_comparison';
const SEGMENT_EFFORT_COMPARISON_SURFACE_IDENTIFIER = 'web_segment_effort_comparison';

const SegmentAnalysisMap = ({ latlngStream, rosterEntries, eventDispatcher, measurementUnits = UNIT_METRIC }: SegmentAnalysisMapProps) => {
  const [athletes, setAthletes] = useState<AthleteRabbitType[]>(() => buildAthletes(rosterEntries));
  const [effortIndex, setEffortIndex] = useState<number | null>(null);

  useEffect(() => {
    const ROSTER_EVENTS = 'setBaseline add remove baselineFetched comparisonFetched';
    const onRosterChange = () => setAthletes(buildAthletes(rosterEntries));
    rosterEntries.on(ROSTER_EVENTS, onRosterChange);
    return () => rosterEntries.off(ROSTER_EVENTS, onRosterChange);
  }, [rosterEntries]);

  useEffect(() => {
    const onChartMouseOver = (_source: any, index: number) => {
      setEffortIndex(index);
    };
    eventDispatcher.on('chartMouseOver', onChartMouseOver);
    return () => eventDispatcher.off('chartMouseOver', onChartMouseOver);
  }, [eventDispatcher]);

  return (
    <StravaPackageContextProviders>
      <UnitFormatsProvider unitSystem={measurementUnits} locale={I18n.language()}>
        <Map
          page={SEGMENT_EFFORT_COMPARISON_ANALYTICS_PAGE}
          category={SEGMENT_EFFORT_COMPARISON_ANALYTICS_CATEGORY}
          surfaceIdentifier={SEGMENT_EFFORT_COMPARISON_SURFACE_IDENTIFIER}
          polyline={latlngStream}
        >
          <Map.Route id="segment" polyline={latlngStream} isSelected={true} disableSelectedEndpoints={true} />
          <Map.UIControls />
          <Map.InitialCameraMode enable3D={false} />
          {effortIndex !== null && athletes.map((athlete) => {
            if (!athlete.latlngStream) return null;
            // d3.bisectLeft can return an index beyond the stream length
            const latLng = athlete.latlngStream[effortIndex];
            if (!latLng) return null;
            return (
              <Map.PlaceHTMLOnMap key={athlete.id} latLng={latLng} className={styles['athlete-rabbit-container']}>
                <AthleteRabbit athlete={athlete} />
              </Map.PlaceHTMLOnMap>
            )
          })}
        </Map>
      </UnitFormatsProvider>
    </StravaPackageContextProviders>
  );
};

export default SegmentAnalysisMap;
